let pdfjsLoader;
let pdfWorkerUrl;

const PDF_PREVIEW_MAX_DIMENSION = 960;
const ACTIVE_STORAGE_REDIRECT_PATH = '/rails/active_storage/blobs/redirect/';
const ACTIVE_STORAGE_PROXY_PATH = '/rails/active_storage/blobs/proxy/';

const sourceFile = file => file?.file || file;

const fileName = file => {
  const source = sourceFile(file);
  return file?.filename || file?.name || source?.filename || source?.name || '';
};

export const isPdfFile = file => {
  const source = sourceFile(file);
  const contentType = file?.content_type || file?.type || source?.type || '';
  return (
    contentType.toLowerCase() === 'application/pdf' ||
    /\.pdf$/i.test(fileName(file))
  );
};

const readFileData = file => {
  const source = sourceFile(file);
  if (source?.arrayBuffer) return source.arrayBuffer();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(source);
  });
};

export const loadPdfjs = () => {
  if (!pdfjsLoader) {
    pdfjsLoader = Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?raw'),
    ])
      .then(([pdfjs, worker]) => {
        pdfWorkerUrl ||= URL.createObjectURL(
          new Blob([worker.default], { type: 'application/javascript' })
        );
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        return pdfjs;
      })
      .catch(error => {
        pdfjsLoader = null;
        throw error;
      });
  }

  return pdfjsLoader;
};

export const getPdfPreviewUrl = url => {
  try {
    const sourceUrl = new URL(url, window.location.origin);

    if (!sourceUrl.pathname.includes(ACTIVE_STORAGE_REDIRECT_PATH)) {
      return url;
    }

    const proxyUrl = new URL(window.location.origin);
    proxyUrl.pathname = sourceUrl.pathname.replace(
      ACTIVE_STORAGE_REDIRECT_PATH,
      ACTIVE_STORAGE_PROXY_PATH
    );
    proxyUrl.search = sourceUrl.search;
    return proxyUrl.toString();
  } catch {
    return url;
  }
};

// Aceleres 2026-08-17: era um `fetch` cru, e por isso todo documento aberto a
// partir do painel de contato caia em "Nao foi possivel exibir este documento".
//
// A URL de la e o endpoint guardado
// (`.../conversations/<id>/attachment_downloads/<id>`), que reavalia a
// permissao a cada pedido e exige o cliente AUTENTICADO — o proprio controller
// avisa que "a URL guardada nao abre sozinha". Os cabecalhos de sessao
// (`access-token`/`client`/`uid`) sao postos pelo interceptor do axios; um
// `fetch` cru nao passa por ele e recebia 401. Medido em 17/08 contra o anexo
// 32968: `curl` sem cabecalhos devolve 401, com os cabecalhos devolve o
// ficheiro.
//
// `window.axios` e o mesmo cliente que a store usa. Para a URL do balao da
// conversa (Active Storage, mesma origem) nada muda: continua a valer o cookie,
// e os cabecalhos a mais sao inofensivos.
export const fetchPdfData = async url => {
  const { data } = await window.axios.get(getPdfPreviewUrl(url), {
    responseType: 'arraybuffer',
  });

  return data;
};

const renderPdfFirstPagePreview = async loadingTask => {
  let pdfDocument;

  try {
    pdfDocument = await loadingTask.promise;
    const page = await pdfDocument.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      2,
      PDF_PREVIEW_MAX_DIMENSION /
        Math.max(baseViewport.width, baseViewport.height)
    );
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { alpha: false });

    if (!context) return null;

    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;

    return {
      preview: canvas.toDataURL('image/jpeg', 0.88),
      pageCount: pdfDocument.numPages,
    };
  } finally {
    if (pdfDocument) {
      await pdfDocument.destroy();
    } else {
      await loadingTask.destroy();
    }
  }
};

export const createPdfFirstPagePreview = async file => {
  if (!isPdfFile(file) || !sourceFile(file)) return null;

  const [pdfjs, fileData] = await Promise.all([
    loadPdfjs(),
    readFileData(file),
  ]);
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(fileData) });

  return renderPdfFirstPagePreview(loadingTask);
};

export const createPdfFirstPagePreviewFromUrl = async url => {
  if (!url) return null;

  const [pdfjs, fileData] = await Promise.all([loadPdfjs(), fetchPdfData(url)]);
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(fileData) });

  return renderPdfFirstPagePreview(loadingTask);
};
